import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, LogOut, Edit, Briefcase, Plus, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CreateCompanyForm } from "@/components/company/CreateCompanyForm";
import { EditCompanyForm } from "@/components/company/EditCompanyForm";
import { CreateJobForm } from "@/components/jobs/CreateJobForm";
import { JobListItem } from "@/components/jobs/JobListItem";
import { EditProfileForm } from "@/components/profile/EditProfileForm";
import type { Company, Job } from "@/types";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Modal states
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  // Mock data - TODO: Replace with actual Convex queries
  const [company, setCompany] = useState<Company | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [profile, setProfile] = useState(null);

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const isEmployer = user?.role === "employer";
  const isSeeker = user?.role === "seeker";

  // Employer handlers
  const handleCreateCompanySuccess = () => {
    setShowCreateCompany(false);
    // TODO: Refetch company data
    alert("Firma opprettet! Vent på godkjenning fra admin.");
  };

  const handleEditCompanySuccess = () => {
    setShowEditCompany(false);
    // TODO: Refetch company data
    alert("Firma oppdatert!");
  };

  const handleCreateJobSuccess = () => {
    setShowCreateJob(false);
    // TODO: Refetch jobs
    alert("Stilling opprettet!");
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    // TODO: Open edit job modal
    alert(`Rediger stilling: ${job.title}`);
  };

  const handleDeleteJob = async (jobId: string) => {
    // TODO: Call deleteJob mutation
    alert(`Sletter stilling: ${jobId}`);
  };

  const handleToggleJobActive = async (jobId: string, active: boolean) => {
    // TODO: Call updateJob mutation
    alert(`Setter stilling ${jobId} til ${active ? "aktiv" : "inaktiv"}`);
  };

  // Seeker handlers
  const handleEditProfileSuccess = () => {
    setShowEditProfile(false);
    // TODO: Refetch profile data
    alert("Profil oppdatert!");
  };

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ backgroundColor: "var(--color-bg-primary)" }}
    >
      <div className="p-4">
        {/* SEEKER VIEW */}
        {isSeeker && (
          <>
            {/* Profile Header */}
            <div
              className="mb-4 rounded-lg p-6 text-center transition-all duration-[var(--motion-duration-medium)]"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
              }}
            >
              <div
                className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full"
                style={{ border: "3px solid var(--color-bg-accent)" }}
              >
                <img
                  src="/kari.jpg"
                  alt="Profilbilde"
                  width={96}
                  height={96}
                  className="h-full w-full object-cover"
                />
              </div>
              <h2
                style={{
                  color: "var(--color-text-primary)",
                  fontSize: "var(--font-size-title-md)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                Kari Hansen
              </h2>
              <p style={{ color: "var(--color-bg-accent)", fontSize: "var(--font-size-body-md)", fontWeight: "var(--font-weight-medium)" }}>
                DevOps Engineer
              </p>
              <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-body-sm)" }}>
                {user?.email || ""}
              </p>
            </div>

            {/* Edit Profile Button */}
            <button
              onClick={() => setShowEditProfile(true)}
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
              style={{
                backgroundColor: "var(--color-bg-accent)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-card)",
              }}
            >
              <Edit className="h-5 w-5" style={{ color: "var(--color-text-primary)" }} />
              <span
                style={{
                  color: "var(--color-text-primary)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                Rediger profil / CV
              </span>
            </button>

            {/* Profile Sections */}
            <div className="space-y-3">
              <div
                className="rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)]"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px solid var(--color-border)",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <h3
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-body-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                    marginBottom: "var(--space-sm)",
                  }}
                >
                  Om meg
                </h3>
                <p
                  style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-body-md)" }}
                >
                  Erfaren DevOps-engineer med spesialisering i cloud-infrastruktur, automatisering og containerteknologi. Brenner for å bygge skalerbare og pålitelige systemer. Jeg har over 5 års erfaring med Kubernetes, AWS og moderne CI/CD-pipelines.
                </p>
              </div>

              <div
                className="rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)]"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px solid var(--color-border)",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <h3
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-body-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                    marginBottom: "var(--space-sm)",
                  }}
                >
                  Erfaring
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4
                      style={{
                        color: "var(--color-text-primary)",
                        fontSize: "var(--font-size-body-md)",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                    >
                      Senior DevOps Engineer
                    </h4>
                    <p
                      className="text-sm"
                      style={{ color: "var(--color-bg-accent)" }}
                    >
                      Cloud Systems AS
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      2019 - Nå • Bergen
                    </p>
                    <p
                      className="mt-2 text-sm"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      Ansvarlig for infrastruktur og deployment-pipelines for flere microservices-applikasjoner. Implementert Kubernetes-cluster og automatisert CI/CD med GitHub Actions.
                    </p>
                  </div>
                  <div>
                    <h4
                      style={{
                        color: "var(--color-text-primary)",
                        fontSize: "var(--font-size-body-md)",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                    >
                      DevOps Engineer
                    </h4>
                    <p
                      className="text-sm"
                      style={{ color: "var(--color-bg-accent)" }}
                    >
                      TechStart AS
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      2017 - 2019 • Oslo
                    </p>
                    <p
                      className="mt-2 text-sm"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      Jobbet med AWS-infrastruktur, Docker-containerisering og Jenkins CI/CD pipelines.
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)]"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px solid var(--color-border)",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <h3
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-body-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                    marginBottom: "var(--space-sm)",
                  }}
                >
                  Utdanning
                </h3>
                <div className="space-y-3">
                  <div>
                    <h4
                      style={{
                        color: "var(--color-text-primary)",
                        fontSize: "var(--font-size-body-md)",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                    >
                      Bachelor i Dataingeniør
                    </h4>
                    <p
                      className="text-sm"
                      style={{ color: "var(--color-bg-accent)" }}
                    >
                      Universitetet i Bergen
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      2015 - 2019
                    </p>
                  </div>
                  <div>
                    <h4
                      style={{
                        color: "var(--color-text-primary)",
                        fontSize: "var(--font-size-body-md)",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                    >
                      Sertifiseringer
                    </h4>
                    <ul className="mt-2 space-y-1 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      <li>• AWS Certified Solutions Architect (2021)</li>
                      <li>• Certified Kubernetes Administrator (CKA) (2020)</li>
                      <li>• Docker Certified Associate (2019)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div
                className="rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)]"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px solid var(--color-border)",
                  boxShadow: "var(--shadow-card)",
                }}
              >
                <h3
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-body-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                    marginBottom: "var(--space-sm)",
                  }}
                >
                  Ferdigheter
                </h3>
                <div className="flex flex-wrap gap-2">
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Kubernetes
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Docker
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    AWS
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Terraform
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    CI/CD
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Python
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    GitHub Actions
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Jenkins
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Ansible
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Prometheus
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-sm font-semibold"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    Grafana
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* EMPLOYER VIEW */}
        {isEmployer && (
          <>
            {company ? (
              <>
                {/* Company Profile */}
                <div
                  className="mb-4 rounded-lg p-6 transition-all duration-[var(--motion-duration-medium)]"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border)",
                    boxShadow: "var(--shadow-card)",
                  }}
                >
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      {company.logo ? (
                        <div
                          className="h-16 w-16 overflow-hidden rounded-lg"
                          style={{ border: "2px solid var(--color-border)" }}
                        >
                          <img
                            src={company.logo}
                            alt={company.name}
                            width={64}
                            height={64}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div
                          className="flex h-16 w-16 items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: "var(--color-bg-accent)",
                            border: "2px solid var(--color-border)",
                          }}
                        >
                          <Building2 className="h-8 w-8" style={{ color: "var(--color-text-primary)" }} />
                        </div>
                      )}
                      <div>
                        <h2
                          style={{
                            color: "var(--color-text-primary)",
                            fontSize: "var(--font-size-title-md)",
                            fontWeight: "var(--font-weight-semibold)",
                          }}
                        >
                          {company.name}
                        </h2>
                        <p
                          className="text-sm"
                          style={{ color: "var(--color-text-secondary)" }}
                        >
                          {company.industry} • {company.location}
                        </p>
                        <span
                          className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                          style={{
                            backgroundColor: company.verified
                              ? "rgba(74, 222, 128, 0.2)"
                              : "rgba(251, 191, 36, 0.2)",
                            color: company.verified ? "#4ade80" : "#fbbf24",
                          }}
                        >
                          {company.verified ? "Verifisert" : "Venter på godkjenning"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowEditCompany(true)}
                      className="rounded-lg p-2 transition-colors hover:bg-gray-700"
                    >
                      <Edit className="h-5 w-5" style={{ color: "var(--color-text-secondary)" }} />
                    </button>
                  </div>

                  <p
                    className="text-sm"
                    style={{ color: "var(--color-text-secondary)" }}
                  >
                    {company.description}
                  </p>
                </div>

                {/* Jobs Section */}
                <div
                  className="mb-4 rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)]"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border)",
                    boxShadow: "var(--shadow-card)",
                  }}
                >
                  <div className="mb-4 flex items-center justify-between">
                    <h3
                      style={{
                        color: "var(--color-text-primary)",
                        fontSize: "var(--font-size-body-lg)",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                    >
                      Mine stillinger ({jobs.length})
                    </h3>
                    <button
                      onClick={() => setShowCreateJob(true)}
                      disabled={!company.verified}
                      className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: "var(--color-bg-accent)",
                        color: "var(--color-text-primary)",
                      }}
                      title={!company.verified ? "Firma må godkjennes først" : "Legg til stilling"}
                    >
                      <Plus className="h-4 w-4" />
                      Legg til stilling
                    </button>
                  </div>

                  {jobs.length === 0 ? (
                    <div className="py-8 text-center">
                      <Briefcase
                        className="mx-auto mb-2 h-12 w-12"
                        style={{ color: "var(--color-text-secondary)" }}
                      />
                      <p
                        className="text-sm"
                        style={{ color: "var(--color-text-secondary)" }}
                      >
                        {company.verified
                          ? "Ingen stillinger lagt til ennå"
                          : "Vent på godkjenning før du kan legge til stillinger"}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {jobs.map((job) => (
                        <JobListItem
                          key={job._id}
                          job={job}
                          onEdit={handleEditJob}
                          onDelete={handleDeleteJob}
                          onToggleActive={handleToggleJobActive}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* No Company - CTA to create */}
                <div
                  className="mb-4 rounded-lg p-8 text-center transition-all duration-[var(--motion-duration-medium)]"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border)",
                    boxShadow: "var(--shadow-card)",
                  }}
                >
                  <div
                    className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                    }}
                  >
                    <Building2 className="h-10 w-10" style={{ color: "var(--color-text-primary)" }} />
                  </div>
                  <h2
                    className="mb-2"
                    style={{
                      color: "var(--color-text-primary)",
                      fontSize: "var(--font-size-title-md)",
                      fontWeight: "var(--font-weight-semibold)",
                    }}
                  >
                    Opprett firmaprofil
                  </h2>
                  <p
                    className="mb-6 text-sm"
                    style={{ color: "var(--color-text-secondary)" }}
                  >
                    For å begynne å publisere stillinger må du først opprette en firmaprofil.
                    Dette tar bare et par minutter.
                  </p>
                  <button
                    onClick={() => setShowCreateCompany(true)}
                    className="rounded-lg px-6 py-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      backgroundColor: "var(--color-bg-accent)",
                      color: "var(--color-text-primary)",
                      boxShadow: "var(--shadow-card)",
                    }}
                  >
                    Opprett firmaprofil
                  </button>
                </div>

                {/* Info cards */}
                <div className="mb-4 space-y-3">
                  <div
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <h3
                      className="mb-2 text-sm font-semibold"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      1. Opprett firmaprofil
                    </h3>
                    <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      Fyll ut informasjon om firmaet ditt
                    </p>
                  </div>
                  <div
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <h3
                      className="mb-2 text-sm font-semibold"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      2. Vent på godkjenning
                    </h3>
                    <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      Vi verifiserer firmaet ditt innen 24 timer
                    </p>
                  </div>
                  <div
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: "var(--color-bg-secondary)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <h3
                      className="mb-2 text-sm font-semibold"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      3. Publiser stillinger
                    </h3>
                    <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      Begynn å motta søknader fra kvalifiserte kandidater
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Common sections for both roles */}
        <div className="mt-4 space-y-3">
          {/* Settings */}
          <button
            className="flex w-full items-center gap-3 rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
            style={{
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-card)",
            }}
          >
            <Settings className="h-5 w-5" style={{ color: "var(--color-text-secondary)" }} />
            <span
              style={{
                color: "var(--color-text-primary)",
                fontWeight: "var(--font-weight-medium)",
              }}
            >
              Innstillinger
            </span>
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg p-4 transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
            style={{
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-card)",
              color: "var(--color-bg-danger)",
            }}
          >
            <LogOut className="h-5 w-5" />
            <span style={{ fontWeight: "var(--font-weight-medium)" }}>Logg ut</span>
          </button>
        </div>
      </div>

      {/* Modals */}
      {showCreateCompany && (
        <CreateCompanyForm
          onClose={() => setShowCreateCompany(false)}
          onSuccess={handleCreateCompanySuccess}
        />
      )}

      {showEditCompany && company && (
        <EditCompanyForm
          company={company}
          onClose={() => setShowEditCompany(false)}
          onSuccess={handleEditCompanySuccess}
        />
      )}

      {showCreateJob && (
        <CreateJobForm
          onClose={() => setShowCreateJob(false)}
          onSuccess={handleCreateJobSuccess}
        />
      )}

      {showEditProfile && (
        <EditProfileForm
          profile={profile}
          onClose={() => setShowEditProfile(false)}
          onSuccess={handleEditProfileSuccess}
        />
      )}
    </div>
  );
}

